import { REQUEST_ENTITIES } from '../entities/types';
import { formatPageBody } from './helper';
import { receiveEntity } from '../entities/actions';
import { uploadMediumFile } from '../story-media/actions';
import * as api from './api';
import * as types from './types';

export const getStoryPages = storyId => ({
  type: REQUEST_ENTITIES,
  promise: api.getStoryPages(storyId),
});

// Moves `pages` (an ordered array of `{ id, media }`, oldest/earliest position
// first) into `newStoryId`. There's no "re-parent a page" endpoint, so this
// reuses the same two primitives the existing "unlink"/"link" page-splitting
// feature (see unlinkStoryPages/linkStoryPages above) is already built on:
// `createStoryPage` accepts a `media` relationship pointing at EXISTING medium
// ids — no re-upload — and `updateStoryPage` can overwrite a page's `media` to
// `[]`. Creating the equivalent page in the new story and then clearing the
// old one's media is effectively "moving" it, the same way linking a page's
// media elsewhere and emptying the source page already works today.
//
// Sequential and best-effort: if a later page fails to move, the earlier ones
// in this call have already moved — same partial-failure characteristics as
// the rest of this app's multi-step mutations (e.g. useAllocation's bulk
// entitlement assignment), surfaced to the caller to report and let the
// member retry rather than attempting a rollback.
export const movePagesToStory = (pages, newStoryId) => async () => {
  for (let index = 0; index < pages.length; index += 1) {
    const page = pages[index];
    // eslint-disable-next-line no-await-in-loop
    await api.createStoryPage(newStoryId, formatPageBody({ position: index, media: page.media }));
    // eslint-disable-next-line no-await-in-loop
    await api.updateStoryPage(page.id, formatPageBody({ media: [] }));
  }
};

export const unlinkStoryPages = (storyId, destPageValues, fromPage) => ({
  type: types.UNLINK_STORY_PAGES,
  promise: api.createStoryPage(storyId, formatPageBody(destPageValues)),
  meta: {
    storyId,
    fromPage,
    destPageValues,
  },
});

export const moveStoryPage = (storyId, pageId, position) => ({
  type: types.MOVE_STORY_PAGE,
  promise: api.moveStoryPage(storyId, pageId, position),
  meta: {
    pageId,
    position,
  },
});

export const linkStoryPages = ({ destPage, fromPage }) => ({
  type: types.LINK_STORY_PAGES,
  promise: api.updateStoryPage(
    destPage.id,
    formatPageBody({
      ...destPage,
      media: [...destPage.media, ...fromPage.media],
    })
  ),
  meta: {
    destPage,
    fromPage,
  },
});

export const changePageOrientation = ({ pageId, pageBody, horizontalOrientation }) => ({
  type: types.CHANGE_PAGE_ORIENTATION,
  promise: api.updateStoryPage(
    pageId,
    formatPageBody({ ...pageBody, 'horizontal-orientation': horizontalOrientation })
  ),
});

export const createStoryPageMedium = (pageId, body) => ({
  type: REQUEST_ENTITIES,
  promise: api.createStoryPageMedium(pageId, body),
});

export const createStoryPageAudioCaption = (pageId, file) => async dispatch => {
  const { name: filename } = file;
  const payload = await api.createStoryPageMedium(pageId, {
    data: { attributes: { type: 'Audio', filename } },
  });
  dispatch(
    receiveEntity({
      payload,
    })
  );
  const [medium] = Object.values(payload.data.entities.audios);
  return dispatch(uploadMediumFile('', medium, file));
};
